import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["step1", "step2", "occupationOption", "positionContainer"]
  static values = {
    positions: Object
  }

  connect() {
    this.selectedOccupations = []
    this.selectedPositions = []
    this.expandedOccupations = new Set()

    // 初期選択状態を取得（職種）
    this.occupationOptionTargets.forEach(label => {
      const input = label.querySelector("input")
      if (input.checked) {
        this.selectedOccupations.push(input.value)
      }
    })

    // 既存のポジション選択状態を取得
    const positionInput = document.getElementById("position-input")
    if (positionInput && positionInput.value) {
      const positionNames = positionInput.value.split(",").map(v => v.trim())
      // 各ポジション名を職種と紐づけて保存
      this.selectedOccupations.forEach(occupation => {
        const positions = this.positionsValue[occupation] || []
        positions.forEach(p => {
          if (positionNames.includes(p.name)) {
            this.selectedPositions.push(`${occupation}:${p.name}`)
          }
        })
      })
    }
  }

  open(event) {
    event.preventDefault()

    const dialogId = event.currentTarget.dataset.occupationPositionDialogId
    if (!dialogId) return

    const dialog = document.getElementById(dialogId)
    if (!dialog) return

    dialog.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  close() {
    this.element.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")

    // ステップ1に戻す
    this.step1Target.classList.remove("hidden")
    this.step2Target.classList.add("hidden")
  }

  selectOccupation(event) {
    const input = event.target
    const label = input.closest("label")

    if (input.checked) {
      label.classList.add("bg-red-50", "border-red-300")
      if (!this.selectedOccupations.includes(input.value)) {
        this.selectedOccupations.push(input.value)
      }
    } else {
      label.classList.remove("bg-red-50", "border-red-300")
      this.selectedOccupations = this.selectedOccupations.filter(v => v !== input.value)
    }
  }

  goToStep2() {
    if (this.selectedOccupations.length === 0) {
      alert("職種を選択してください")
      return
    }

    // ポジション選択UIを構築
    this.buildPositionUI()

    // ステップ切り替え
    this.step1Target.classList.add("hidden")
    this.step2Target.classList.remove("hidden")
  }

  goToStep1() {
    this.step1Target.classList.remove("hidden")
    this.step2Target.classList.add("hidden")
  }

  buildPositionUI() {
    const container = this.positionContainerTarget
    container.innerHTML = ""

    this.selectedOccupations.forEach(occupation => {
      const positions = this.positionsValue[occupation] || []

      // アコーディオンセクション
      const section = document.createElement("div")
      section.className = "border-b border-gray-200"

      // ヘッダー（職種名）
      const header = document.createElement("button")
      header.type = "button"
      header.className = "w-full flex items-center justify-between py-3 px-2 text-left hover:bg-gray-50"
      header.innerHTML = `
        <span class="text-sm font-medium text-gray-900">${occupation}</span>
        <svg class="w-5 h-5 text-gray-400 transform transition-transform ${this.expandedOccupations.has(occupation) ? 'rotate-180' : ''}" data-chevron fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      `
      header.addEventListener("click", () => this.toggleAccordion(occupation, section))

      // コンテンツ（ポジションリスト）
      const content = document.createElement("div")
      content.className = `pl-4 pb-2 ${this.expandedOccupations.has(occupation) ? '' : 'hidden'}`
      content.dataset.occupationContent = occupation

      positions.forEach(position => {
        const label = document.createElement("label")
        label.className = "flex items-center justify-between p-2 rounded-sm hover:bg-gray-50 cursor-pointer"

        const isSelected = this.selectedPositions.includes(`${occupation}:${position.name}`)

        label.innerHTML = `
          <span class="text-sm text-gray-700 ml-2">${position.name}</span>
          <input type="checkbox"
                 name="position_selection"
                 value="${occupation}:${position.name}"
                 class="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded-sm"
                 ${isSelected ? 'checked' : ''}>
        `

        const input = label.querySelector("input")
        input.addEventListener("change", (e) => this.selectPosition(e))

        content.appendChild(label)
      })

      section.appendChild(header)
      section.appendChild(content)
      container.appendChild(section)

      // デフォルトで展開
      if (!this.expandedOccupations.has(occupation)) {
        this.expandedOccupations.add(occupation)
        content.classList.remove("hidden")
        header.querySelector("[data-chevron]").classList.add("rotate-180")
      }
    })
  }

  toggleAccordion(occupation, section) {
    const content = section.querySelector(`[data-occupation-content="${occupation}"]`)
    const chevron = section.querySelector("[data-chevron]")

    if (this.expandedOccupations.has(occupation)) {
      this.expandedOccupations.delete(occupation)
      content.classList.add("hidden")
      chevron.classList.remove("rotate-180")
    } else {
      this.expandedOccupations.add(occupation)
      content.classList.remove("hidden")
      chevron.classList.add("rotate-180")
    }
  }

  selectPosition(event) {
    const input = event.target
    const value = input.value

    if (input.checked) {
      if (!this.selectedPositions.includes(value)) {
        this.selectedPositions.push(value)
      }
    } else {
      this.selectedPositions = this.selectedPositions.filter(v => v !== value)
    }
  }

  confirm() {
    // hidden inputの値を更新
    const occupationInput = document.getElementById("occupation-input")
    const positionInput = document.getElementById("position-input")

    if (occupationInput) {
      occupationInput.value = this.selectedOccupations.join(",")
    }

    if (positionInput) {
      // 職種:ポジション形式で保存
      positionInput.value = this.selectedPositions.join(",")
    }

    // 統合された表示を更新
    const detailDisplay = document.getElementById("occupation-detail-display")
    if (detailDisplay) {
      detailDisplay.innerHTML = this.buildIntegratedDisplay()
    }

    this.close()

    // カスタムイベントを発行
    this.dispatch("selected", {
      detail: {
        occupations: this.selectedOccupations,
        positions: this.selectedPositions
      }
    })
  }

  // 職種ごとにポジションをまとめた表示HTMLを生成
  buildIntegratedDisplay() {
    if (this.selectedOccupations.length === 0) {
      return '<span class="text-sm text-gray-400">未設定</span>'
    }

    // ポジションを職種ごとにグループ化
    const positionsByOccupation = this.groupItemsByOccupation(this.selectedPositions)

    // 職種ごとにHTMLを生成
    const html = this.selectedOccupations.map(occupation => {
      const positions = positionsByOccupation[occupation] || []

      let contentHtml = ''

      if (positions.length > 0) {
        const badges = positions.map(name =>
          `<span class="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-purple-50 text-purple-700 ml-1">${name}</span>`
        ).join("")
        contentHtml = `<div><span class="text-xs text-gray-500">ポジション:</span>${badges}</div>`
      } else {
        contentHtml = '<span class="text-xs text-gray-400">ポジション 未設定</span>'
      }

      return `
        <div class="mb-3">
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">${occupation}</span>
          <div class="ml-4 mt-1 v3-space-y-1">
            ${contentHtml}
          </div>
        </div>
      `
    }).join("")

    return html
  }

  // アイテムを職種ごとにグループ化
  groupItemsByOccupation(items) {
    const grouped = {}
    items.forEach(item => {
      const parts = item.split(":")
      if (parts.length === 2) {
        const [occupation, name] = parts
        if (!grouped[occupation]) {
          grouped[occupation] = []
        }
        grouped[occupation].push(name)
      }
    })
    return grouped
  }
}
